import { action, computed, observable } from 'mobx';
import { UtilsService } from '@app/src/services/UtilsService';
import { DateTimeService } from '@app/src/services/DateTimeService';

export interface IValidation {
  callback: (value: any) => boolean;
  message: string;
  params?: { [key: string]: any };
}

export enum FieldType {
  STRING,
  NUMBER,
  BOOLEAN,
  ARRAY,
  DATE,
  FILE,
  BYTE,
  OBJECT,
  EMAIL,
  LAYOUT,
  STANDARD
}

export enum DatePrefill {
  CURRENT = 'current',
  CURRENT_START = 'current_start',
  CURRENT_END = 'current_end'
}

export class MobxFieldControl {
  _fieldType: FieldType;
  _onChanges: (value: any, oldValue: any) => void;
  isMandatory: boolean = false;
  validators: IValidation[];
  skipValidators: any[];
  skipCheckValidators: any[];
  formatter?: (value: any) => any;
  defaultValue: any;

  @observable isTouched: boolean;
  @observable _value: any;

  @computed get displayValue(): any {
    return !this.formatter ? this._value : this.formatter(this._value);
  }

  @computed get value(): any {
    if (this._fieldType === FieldType.BYTE) {
      return !this._value ? 0 : UtilsService.binaryToDecimal(this._value);
    }
    if (this._fieldType === FieldType.NUMBER) {
      return !this._value && this._value !== 0 ? void 0 : +this._value;
    }
    return this._value;
  }

  @computed get isSkipped(): boolean {
    return !!this.skipValidators.find(callback => callback(this.value));
  }

  @computed get isSkippedCheck(): boolean {
    return !!this.skipCheckValidators.find(callback => callback(this.value));
  }

  @computed get isValid(): boolean {
    if (this.isSkipped || this.isSkippedCheck) {
      return true;
    }
    return this.callFieldValidators();
  }

  @computed get errors(): string[] {
    if (this.isSkipped || this.isSkippedCheck) {
      return [];
    }
    return this.validators
      .filter(validation => !validation.callback(this.value))
      .map(validation => validation.message);
  }

  @computed get error() {
    return this.getErrorDirectly();
  }

  get onChanges() {
    return {
      subscribe: (callback: (value: any, valueOld: any) => void) => {
        this._onChanges = callback;
      }
    };
  }

  constructor(
    value: string | number | boolean | any[],
    fieldType: FieldType = FieldType.STRING,
    validators: any[] = [],
    skipValidators: any[] = [],
    skipCheckValidators: any[] = []
  ) {
    this.isTouched = false;
    this._value = value;
    this.validators = validators;
    this.skipValidators = skipValidators;
    this.skipCheckValidators = skipCheckValidators;
    this._fieldType = fieldType;
    this.defaultValue =
      value &&
      [FieldType.OBJECT, FieldType.ARRAY, FieldType.FILE].includes(fieldType)
        ? Object.create(value as any)
        : value;
    if (value) {
      if (
        [FieldType.OBJECT, FieldType.ARRAY, FieldType.FILE].includes(fieldType)
      ) {
        value = Object.create(value as any);
      } else if (fieldType === FieldType.DATE) {
        switch (value) {
          case DatePrefill.CURRENT:
            const currentDate = new Date();
            value = currentDate.toISOString();
            this._value = value;
            break;
          case DatePrefill.CURRENT_END:
            value = DateTimeService.getTodaysDay().endIso;
            this._value = value;
            break;
          case DatePrefill.CURRENT_START:
            value = DateTimeService.getTodaysDay().startIso;
            this._value = value;
            break;
          default:
            break;
        }
      }
    }
    this.defaultValue = value;
  }

  @action
  setValue(
    value: any,
    isTouched: boolean = true,
    triggerChange: boolean = true
  ) {
    const oldValue = this._value;
    this._value = value;
    this.isTouched = isTouched;
    if (this._onChanges && triggerChange) {
      this._onChanges(this._value, oldValue);
    }
  }

  @action
  clear() {
    this._value = this._fieldType === FieldType.BOOLEAN ? false : undefined;
    this.isTouched = false;
  }

  @action
  reset() {
    this._value =
      this.defaultValue &&
      [FieldType.OBJECT, FieldType.ARRAY, FieldType.FILE].includes(
        this._fieldType
      )
        ? Object.create(this.defaultValue as any)
        : this.defaultValue;
    this.isTouched = false;
  }

  @action
  resetIsTouched() {
    this.isTouched = false;
  }

  getErrorDirectly = () => {
    if (this.isSkipped) {
      return;
    }
    const error = this.validators.find(
      validation => !validation.callback(this.value)
    );
    if (error) {
      return {
        message: error.message,
        params: error.params
      };
    }
  };

  addValidator(validator: IValidation) {
    this.validators.push(validator);
  }

  removeValidator(message: string) {
    this.validators = this.validators.filter(
      validator => validator.message !== message
    );
  }

  addSkipValidator(validator: any) {
    this.skipValidators.push(validator);
  }

  addSkipCheckValidator(validator: any) {
    this.skipCheckValidators.push(validator);
  }

  getValidator(message: string) {
    return this.validators.find(validator => {
      return validator.message === message;
    });
  }

  callFieldValidators(): boolean {
    return !this.validators.find(
      validation => !validation.callback(this.value)
    );
  }
}
